import * as React from 'react';
import { useStyles } from './styles';
import Box from '@mui/material/Box';
import { useEffect } from 'react';
import { UnregisterTouchToMouse } from '@core/dashboard/components/DashboardContent/GameCast/touchtomouse';
import { useGamecastCursor } from './context/context';

/**
 * Renders the media elements the GameLift Streams SDK streams the experience
 * into, and applies the cursor UE reports for that viewport (MV-1820).
 *
 * The cursor is an inline style rather than an imperative write so React stays
 * the only writer of that property; `styles.ts` marks the geometry
 * `!important` but not the cursor, so this still wins. It rides on the video
 * element alone — the wrapper takes no pointer events, and the hub's own UI
 * keeps its own cursors.
 *
 * Being the only writer is load-bearing: React re-applies the style only when
 * the value changes, so anything else writing `style.cursor` would desync the
 * DOM from what React thinks it rendered and stay wrong until UE reports a
 * different state. GameLift Streams 1.3.0 is configured `setCursor:
 * 'visibility'` and its bundle contains no `style` write at all, so nothing
 * competes today — but an SDK upgrade that starts hiding the cursor itself has
 * to move this write into the message handler, where the 1 Hz snapshot
 * re-asserts it.
 */
const VideoGamecast = () => {
  const classes = useStyles();
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const cursor = useGamecastCursor((state) => state.cursor);
  const resetCursor = useGamecastCursor((state) => state.resetState);

  useEffect(() => {
    return () => {
      const videoElement = document.getElementById('gamecastVideoTag');

      if (videoElement) {
        UnregisterTouchToMouse(videoElement);
      }

      // The store outlives this element, so the next session would otherwise
      // open on the cursor the last one ended with.
      resetCursor();
    };
  }, [resetCursor]);

  return (
    <Box sx={classes.root}>
      <video
        ref={videoRef}
        playsInline
        autoPlay
        id="gamecastVideoTag"
        data-testid="gamelift-streams-video"
        muted
        style={{ cursor }}
        onContextMenu={(event) => {
          event.preventDefault();
          return false;
        }}
        onPause={() => videoRef.current?.play()}
      ></video>
      <audio
        autoPlay
        muted
        id="gamecastAudioTag"
        data-testid="gamelift-streams-audio"
      />
    </Box>
  );
};

export default VideoGamecast;
